import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { errorMessageFor } from '../../auth/error-message';
import { ApiError } from '../../api/api-error';
import { downloadBlob } from '../../api/binary';
import { importApi as defaultImportApi } from './import-api';
import { UploadStep } from './steps/UploadStep';
import { MappingStep } from './steps/MappingStep';
import { PreviewStep } from './steps/PreviewStep';
import { ConfirmStep } from './steps/ConfirmStep';
import './import.css';

const TITLE = 'Importar socios';
const RESULT_FALLBACK_NAME = 'importacion-socios.xlsx';
const STEPS = [
  { id: 'upload', label: 'Subir archivo' },
  { id: 'mapping', label: 'Mapear columnas' },
  { id: 'preview', label: 'Vista previa' },
  { id: 'confirm', label: 'Confirmar' },
];

function initialState() {
  return {
    step: 'upload',
    upload: null, // { uploadId, columns, sampleRows, totalRows, suggestedMapping }
    mapping: null, // last submitted mapping — prefills MappingStep on "volver"
    updateExisting: false,
    preview: null, // { summary, rows, plansToCreate, notImportedColumns }
    commitStatus: 'ready', // ready | committing | done | error
    commitResult: null, // { blob, filename, summary }
    error: '',
    errorKind: 'other',
  };
}

// Classifies a failed `commit`/`downloadResult` call into what ConfirmStep
// should offer next (see its own doc comment). `resultAvailable` travels on
// the ApiError itself (member-import-routes.js nests it inside the `{ error
// }` envelope on a 409; `fetchBlob` copies it onto the thrown instance — see
// ../../api/binary.js).
function classifyError(error) {
  if (error instanceof ApiError) {
    if (error.status === 404 && error.code === 'import_upload_not_found') return 'expired';
    if (error.status === 409 && error.code === 'import_already_committed') {
      return error.resultAvailable ? 'already_committed_result' : 'already_committed';
    }
    if (error.status === 0 && error.code === 'network_error') return 'network_uncertain';
  }
  return 'other';
}

/**
 * `/admin/socios/importar` (HAMMER 1D plan, task E6): a four-step wizard over
 * the E3 API (`import-api.js`) — subir → mapear → vista previa → confirmar.
 * Every step's async result lives here, not in the step component, so going
 * back never loses what the admin already entered, and each step stays a
 * plain, easily-testable view over props.
 */
export function ImportMembersPage({ api = defaultImportApi, downloadFile = downloadBlob }) {
  const { handleApiError } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(initialState);
  const [busy, setBusy] = useState(false);

  // Returns true when AuthContext already handled the failure (401 / a
  // pending password change) — callers stop right there, since the app is
  // about to navigate away and there is nothing left for this page to show.
  function reportFailure(caught) {
    const outcome = handleApiError(caught);
    if (outcome.handled) return true;
    setState((prev) => ({ ...prev, error: errorMessageFor(caught), errorKind: classifyError(caught) }));
    return false;
  }

  async function handleUpload(file) {
    setBusy(true);
    setState((prev) => ({ ...prev, error: '' }));
    try {
      const result = await api.upload(file);
      setState(() => ({
        ...initialState(), step: 'mapping', upload: result, mapping: result.suggestedMapping,
      }));
    } catch (caught) {
      reportFailure(caught);
    } finally {
      setBusy(false);
    }
  }

  async function runPreview(mapping, updateExisting) {
    setBusy(true);
    setState((prev) => ({ ...prev, error: '' }));
    try {
      const result = await api.preview({ uploadId: state.upload.uploadId, mapping, updateExisting });
      setState((prev) => ({
        ...prev, step: 'preview', mapping, updateExisting, preview: result, error: '',
      }));
    } catch (caught) {
      reportFailure(caught);
    } finally {
      setBusy(false);
    }
  }

  function handleMappingSubmit(mapping) {
    runPreview(mapping, state.updateExisting);
  }

  function handleToggleUpdateExisting(value) {
    runPreview(state.mapping, value);
  }

  function handleBackToMapping() {
    setState((prev) => ({ ...prev, step: 'mapping', error: '' }));
  }

  function handleBackToUpload() {
    setState(initialState());
  }

  function handleGoToConfirm() {
    setState((prev) => ({
      ...prev, step: 'confirm', commitStatus: 'ready', commitResult: null, error: '',
    }));
  }

  function handleBackToPreview() {
    setState((prev) => ({ ...prev, step: 'preview', commitStatus: 'ready', error: '' }));
  }

  function applyDownloadableResult(result) {
    setState((prev) => ({
      ...prev, commitStatus: 'done', commitResult: result, error: '',
    }));
    downloadFile(result.blob, result.filename || RESULT_FALLBACK_NAME);
  }

  async function handleCommit() {
    // Guards a double click/Enter from firing a second request while the
    // first is still in flight — the API's own idempotency (409 on a second
    // commit of the same uploadId) is the last line of defense, not the
    // first.
    if (state.commitStatus === 'committing') return;
    setState((prev) => ({ ...prev, commitStatus: 'committing', error: '' }));
    try {
      const result = await api.commit({ uploadId: state.upload.uploadId, mapping: state.mapping, updateExisting: state.updateExisting });
      applyDownloadableResult(result);
    } catch (caught) {
      const handled = reportFailure(caught);
      if (handled) return;
      setState((prev) => ({ ...prev, commitStatus: 'error' }));
    }
  }

  // Re-download of an import that DID complete (the retry got `409
  // import_already_committed` with `resultAvailable: true`) or whose outcome
  // this client never learned (a network error while the commit was in
  // flight) — `GET /members/import/:uploadId/result`, per the E3 fix round.
  async function handleDownloadResult() {
    try {
      const result = await api.downloadResult(state.upload.uploadId);
      applyDownloadableResult(result);
    } catch (caught) {
      const handled = reportFailure(caught);
      if (handled) return;
      setState((prev) => ({ ...prev, commitStatus: 'error' }));
    }
  }

  function handleDownloadAgain() {
    if (state.commitResult) downloadFile(state.commitResult.blob, state.commitResult.filename || RESULT_FALLBACK_NAME);
  }

  function handleRestart() {
    setState(initialState());
  }

  function handleGoToMembers() {
    navigate('/admin/socios');
  }

  const canLeave = state.commitStatus !== 'committing';

  return (
    <section className="gym-page" aria-labelledby="gym-page-title">
      <header className="gym-page__header gym-import-header">
        <h1 id="gym-page-title" className="gym-page__title">{TITLE}</h1>
        {canLeave && <Link to="/admin/socios" className="gym-admin-members-link">Volver a Socios</Link>}
      </header>

      <ol className="gym-import-steps" aria-label="Progreso de la importación">
        {STEPS.map((item, index) => (
          <li
            key={item.id}
            className={[
              'gym-import-step',
              item.id === state.step ? 'gym-import-step--active' : '',
              index < STEPS.findIndex(stepItem => stepItem.id === state.step) ? 'gym-import-step--complete' : '',
            ].filter(Boolean).join(' ')}
            aria-current={item.id === state.step ? 'step' : undefined}
          >
            <span className="gym-import-step__number">{index + 1}</span>
            <span>{item.label}</span>
          </li>
        ))}
      </ol>

      {state.step === 'upload' && (
        <UploadStep onUpload={handleUpload} submitting={busy} error={state.error} />
      )}

      {state.step === 'mapping' && state.upload && (
        <MappingStep
          columns={state.upload.columns}
          sampleRows={state.upload.sampleRows}
          totalRows={state.upload.totalRows}
          initialMapping={state.mapping || state.upload.suggestedMapping}
          onSubmit={handleMappingSubmit}
          onCancel={handleBackToUpload}
          submitting={busy}
          error={state.error}
        />
      )}

      {state.step === 'preview' && state.preview && (
        <PreviewStep
          summary={state.preview.summary}
          rows={state.preview.rows}
          plansToCreate={state.preview.plansToCreate}
          notImportedColumns={state.preview.notImportedColumns}
          updateExisting={state.updateExisting}
          onToggleUpdateExisting={handleToggleUpdateExisting}
          onBack={handleBackToMapping}
          onConfirm={handleGoToConfirm}
          loading={busy}
          error={state.error}
        />
      )}

      {state.step === 'confirm' && state.preview && (
        <ConfirmStep
          summary={state.preview.summary}
          updateExisting={state.updateExisting}
          status={state.commitStatus}
          error={state.error}
          errorKind={state.errorKind}
          result={state.commitResult}
          onConfirm={handleCommit}
          onBack={handleBackToPreview}
          onDownloadAgain={handleDownloadAgain}
          onDownloadResult={handleDownloadResult}
          onRestart={handleRestart}
          onGoToMembers={handleGoToMembers}
        />
      )}
    </section>
  );
}
